import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Pencil, Phone, Plus, UserX2, Users2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState, NoMatchesState } from '@/components/ListStates';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import { formatMetaInsightsValue } from '@/utils/currency';
import { useCompanyCurrency, useLabels } from '@/contexts/WorkspaceContext';
import {
  staffService, STAFF_ROLE_SUGGESTIONS, type StaffMember, type CreateStaffMemberInput,
} from '@/services/staff.service';

const COLOR_SWATCHES = ['#a18a44', '#3b82f6', '#22c55e', '#ef4444', '#a855f7', '#f97316', '#14b8a6', '#ec4899'];

interface Props {
  staff: StaffMember[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  isAdmin: boolean;
  onChanged: () => void;
  /** Open the add form on arrival — set when the rota's "Add staff" sent the user here. */
  openNewForm?: boolean;
  onNewFormOpened?: () => void;
}

export default function StaffDirectory({ staff, loading, error, onRetry, isAdmin, onChanged, openNewForm, onNewFormOpened }: Props) {
  const t = useLabels();
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<StaffMember | 'new' | null>(null);

  useEffect(() => {
    if (!openNewForm) return;
    if (isAdmin) setEditing('new');
    onNewFormOpened?.();
  }, [openNewForm, isAdmin, onNewFormOpened]);

  const filtered = staff.filter((s) => {
    if (!showInactive && !s.isActive) return false;
    const text = q.trim().toLowerCase();
    if (!text) return true;
    return s.name.toLowerCase().includes(text) || s.role.toLowerCase().includes(text) || (s.phone ?? '').toLowerCase().includes(text);
  });

  const remove = async (s: StaffMember) => {
    if (!window.confirm(`Deactivate ${s.name}? Past shifts stay on record; future shifts should be reassigned.`)) return;
    try { await staffService.removeStaff(s.staffUid); toast.success('Staff member deactivated'); onChanged(); }
    catch (e: any) { if (!e?.response) toast.error('Could not deactivate this staff member.'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search staff…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-9 w-full max-w-xs text-xs"
        />
        <button
          type="button"
          onClick={() => setShowInactive((v) => !v)}
          className={clsx(
            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-2xs font-semibold whitespace-nowrap transition',
            showInactive ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
          )}
        >
          Show inactive
        </button>
        {isAdmin && (
          <Button size="sm" iconLeft={<Plus size={14} />} className="ml-auto" onClick={() => setEditing('new')}>
            Add {t('staff', 'singular').toLowerCase()}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-28 rounded-2xl" />)}
        </div>
      ) : error ? (
        <LoadErrorState what="your staff" onRetry={onRetry} />
      ) : filtered.length === 0 && q.trim() ? (
        <NoMatchesState onClear={() => setQ('')} />
      ) : filtered.length === 0 && staff.length > 0 ? (
        <EmptyState
          icon={<Users2 size={26} />}
          title="No active staff"
          description="Everyone on the team is deactivated. Show inactive staff to review them, or add someone new."
          action={
            <>
              <Button variant="secondary" onClick={() => setShowInactive(true)}>Show inactive</Button>
              {isAdmin && <Button iconLeft={<Plus size={14} />} onClick={() => setEditing('new')}>Add {t('staff', 'singular').toLowerCase()}</Button>}
            </>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Users2 size={26} />}
          title="No staff yet"
          description="Add your team to start building the weekly rota."
          action={isAdmin ? <Button iconLeft={<Plus size={14} />} onClick={() => setEditing('new')}>Add {t('staff', 'singular').toLowerCase()}</Button> : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((s) => <StaffCard key={s.staffUid} s={s} isAdmin={isAdmin} onEdit={() => setEditing(s)} onRemove={() => remove(s)} />)}
        </div>
      )}

      <StaffFormModal
        open={!!editing}
        staff={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); onChanged(); }}
      />
    </div>
  );
}

function StaffCard({ s, isAdmin, onEdit, onRemove }: { s: StaffMember; isAdmin: boolean; onEdit: () => void; onRemove: () => void }) {
  const initials = s.name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
  return (
    <Card className={clsx('flex items-start gap-3', !s.isActive && 'opacity-60')}>
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
        style={{ backgroundColor: s.color }}
      >
        {initials}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-medium text-text-primary">{s.name}</span>
          {!s.isActive && <span className="rounded-full bg-surface-alt px-1.5 py-0.5 text-[10px] font-semibold text-text-muted">Inactive</span>}
        </div>
        <div className="text-2xs text-text-muted">{s.role}</div>
        {s.phone && (
          <div className="mt-1 flex items-center gap-1 text-2xs text-text-secondary">
            <Phone size={11} />{s.phone}
          </div>
        )}
        {s.hourlyRate != null && (
          <div className="mt-1 text-2xs text-text-secondary">
            {formatMetaInsightsValue(String(s.hourlyRate), s.currency ?? 'USD')}/hr
          </div>
        )}
      </div>
      {isAdmin && (
        <div className="flex shrink-0 flex-col gap-1">
          <button onClick={onEdit} className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary" aria-label="Edit">
            <Pencil size={13} />
          </button>
          {s.isActive && (
            <button onClick={onRemove} className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-danger-soft hover:text-danger" aria-label="Deactivate">
              <UserX2 size={13} />
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

function StaffFormModal({
  open, staff, onClose, onSaved,
}: { open: boolean; staff: StaffMember | null; onClose: () => void; onSaved: () => void }) {
  const companyCurrency = useCompanyCurrency();
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [phone, setPhone] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [currency, setCurrency] = useState(companyCurrency);
  const [color, setColor] = useState(COLOR_SWATCHES[0]);
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (staff) {
      setName(staff.name); setRole(staff.role); setPhone(staff.phone ?? '');
      setHourlyRate(staff.hourlyRate != null ? String(staff.hourlyRate) : '');
      setCurrency(staff.currency ?? companyCurrency);
      setColor(staff.color); setNotes(staff.notes ?? ''); setIsActive(staff.isActive);
    } else {
      setName(''); setRole(''); setPhone(''); setHourlyRate(''); setCurrency(companyCurrency);
      setColor(COLOR_SWATCHES[Math.floor(Math.random() * COLOR_SWATCHES.length)]); setNotes(''); setIsActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, staff]);

  const save = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!role.trim()) { toast.error('Role is required'); return; }
    const rate = hourlyRate.trim() ? Number(hourlyRate) : undefined;
    if (hourlyRate.trim() && (Number.isNaN(rate) || (rate as number) < 0)) { toast.error('Hourly rate must be a positive number'); return; }
    setBusy(true);
    const payload: CreateStaffMemberInput = {
      name: name.trim(), role: role.trim(), phone: phone.trim() || undefined,
      hourlyRate: rate, currency: rate !== undefined ? currency : undefined,
      color, notes: notes.trim() || undefined, isActive,
    };
    try {
      if (staff) await staffService.updateStaff(staff.staffUid, payload);
      else await staffService.createStaff(payload);
      toast.success(staff ? 'Staff member updated' : 'Staff member added');
      onSaved();
    } catch (e: any) { if (!e?.response) toast.error('Could not save this staff member.'); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title={staff ? 'Edit staff member' : 'Add staff member'} widthClass="max-w-lg">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Name" required><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ali Hassan" /></FormField>
        <FormField label="Role" required hint="e.g. Waiter, Chef">
          <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Waiter" list="staff-role-suggestions" />
          <datalist id="staff-role-suggestions">
            {STAFF_ROLE_SUGGESTIONS.map((r) => <option key={r} value={r} />)}
          </datalist>
        </FormField>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Phone"><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+971500000000" /></FormField>
        <FormField label="Hourly rate" hint="Optional">
          <div className="flex gap-2">
            <div className="min-w-0 flex-1"><Input type="number" min={0} step="0.01" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} placeholder="0.00" /></div>
            <div className="w-20 shrink-0"><Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} className="text-center uppercase" aria-label="Currency" /></div>
          </div>
        </FormField>
      </div>
      <FormField label="Colour" hint="Used on the rota">
        <div className="flex flex-wrap gap-2">
          {COLOR_SWATCHES.map((c) => (
            <button
              key={c} type="button" onClick={() => setColor(c)}
              className={clsx('h-7 w-7 rounded-full border-2 transition', color === c ? 'border-text-primary scale-110' : 'border-transparent')}
              style={{ backgroundColor: c }}
              aria-label={c}
            />
          ))}
        </div>
      </FormField>
      <FormField label="Notes"><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></FormField>
      {staff && (
        <FormField label="Status">
          <Select value={isActive ? 'active' : 'inactive'} onChange={(e) => setIsActive(e.target.value === 'active')}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
        </FormField>
      )}
      <div className="mt-4 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={busy} disabled={busy}>{staff ? 'Save' : 'Add'}</Button>
      </div>
    </Modal>
  );
}
